/**
 * 批量任务类型定义 (智能延迟解析架构)
 *
 * 与后端模型对齐：
 * - BatchSessionPhase: draft|ready|running|completed|failed|partial|cancelled
 * - BatchFileStatus: uploaded|parsing|ready|processing|completed|failed
 * - WorkItemStatus: pending|running|succeeded|failed|skipped|cancelled
 */

// ============= 基础类型 =============

/**
 * BatchSession 状态（Phase）
 * 对应后端 BatchSessionPhase
 */
export type BatchSessionPhase =
  | 'draft'           // 草稿（刚创建）
  | 'ready'           // 就绪（文件已关联）
  | 'running'         // 运行中
  | 'completed'       // 已完成
  | 'failed'          // 失败
  | 'partial'         // 部分完成
  | 'cancelled'       // 已取消

/**
 * BatchFile 状态（只负责解析，不负责转换）
 * 对应后端 BatchFileStatus
 */
export type BatchFileStatus =
  | 'uploaded'        // 已上传
  | 'parsing'         // 解析中
  | 'ready'           // 解析完成，就绪
  | 'failed'          // 解析失败

/**
 * WorkItem 状态
 * 对应后端 WorkItemStatus
 */
export type WorkItemStatus =
  | 'pending'         // 等待中
  | 'running'         // 执行中
  | 'succeeded'       // 成功
  | 'failed'          // 失败
  | 'skipped'         // 跳过
  | 'cancelled'       // 已取消

/**
 * Bundle 打包状态
 * 对应后端 BundleState
 */
export type BundleState =
  | 'none'            // 未打包
  | 'building'        // 打包中
  | 'ready'           // 就绪
  | 'expired'         // 已过期
  | 'failed'          // 失败

/**
 * 文件类型
 * 对应后端 BatchFileType
 */
export type BatchFileType = 'text' | 'markdown' | 'srt' | 'ass' | 'ssa'

// ============= 文件处理相关 =============

/**
 * BatchFile 批量文件
 * 对应后端 model.BatchFile
 */
export interface BatchFile {
  id: number
  fileUid: string
  userId: number

  // 文件基本信息
  originalName: string
  fileSize: number
  fileType: BatchFileType
  contentHash: string

  // 文件内容
  rawContent: string

  // 解析结果（V1.0简化）
  parsedContent?: string
  totalChars: number
  progress: number

  // 状态管理
  status: BatchFileStatus
  errorMessage?: string

  // 检测信息
  detectedLanguage?: string

  // V1.0解析响应字段
  estimateSeconds?: number      // 预计时长（秒）
  estimateCredits?: number      // 预计消耗额度
  encoding?: string             // 文件编码
  baseCharsPerSec?: number      // 基线字符/秒（4.5）
  warnings?: string[]           // 警告信息

  // 时间信息
  createdAt: string
  updatedAt: string
}

// ============= 批次会话 =============

/**
 * BatchSession 批次会话
 * 对应后端 model.BatchSession
 */
export interface BatchSession {
  id: number
  sessionUid: string
  userId: number

  // 基本信息
  title: string
  description: string

  // 全局配置
  voice: string
  audioFormat: string
  rate: number
  pitch: number
  style: string

  // 统计信息
  totalFiles: number
  totalSegments: number
  totalChars: number
  completedFiles: number
  failedFiles: number
  progress: number // 0-100

  // 状态信息
  phase: BatchSessionPhase
  errorMessage?: string

  // 下载信息（Bundle）
  bundleUrl?: string
  bundleState: BundleState
  bundleReadyAt?: string
  bundleExpiresAt?: string
  bundleEtag?: string
  bundleCountTotal: number
  bundleCountSucceeded: number
  bundleSizeBytes?: number

  // 时间信息
  createdAt: string
  updatedAt: string
  submittedAt?: string
  finishedAt?: string

  // 关联数据（可选加载）
  files?: BatchFile[]
  workItems?: WorkItem[]
}

// ============= 工作项 =============

/**
 * WorkItem 执行单元 - 一个文件的一次 TTS 转换任务
 * 对应后端 model.WorkItem
 */
export interface WorkItem {
  id: number
  workItemUid: string

  // 关联关系
  sessionUid: string
  fileUid: string
  userId: number

  // 顺序信息
  sequenceIndex: number // 在会话中的顺序（1-based）

  // 执行状态
  status: WorkItemStatus
  errorMessage?: string

  // 重试信息
  attemptCount: number
  maxAttempts: number
  priority: number

  // 执行信息
  providerJobId?: string  // 第三方任务ID（用于追踪和调试）
  engine?: string  // 使用的引擎

  // 音频结果（主成果物）
  audioUrl?: string
  downloadUrl?: string  // 预签名下载链接
  providerResultUrl?: string  // 第三方下载链接（保底方案）
  audioFormat?: string
  audioSize?: number
  durationMs?: number

  // 字幕结果（可选成果物）
  subtitleUrl?: string
  subtitleFormat?: string

  // 摘要结果（可选成果物）
  summaryUrl?: string
  summaryFormat?: string

  // Word boundary 信息（用于字幕生成）
  wordBoundaryUrl?: string
  wordBoundaryFormat?: string

  // 时间信息
  createdAt: string
  updatedAt: string
  startedAt?: string
  completedAt?: string
  processingTime?: number
}

// ============= UI配置类型 =============

/**
 * 全局主播配置
 */
export interface GlobalVoiceConfig {
  voice: string
  voiceName: string
  rate: number
  pitch: number
  style: string
  source: 'manual' | 'auto'
}

/**
 * 文件级主播配置覆盖
 */
export interface FileVoiceConfig {
  fileIndex: number
  voice: string | null
  voiceName: string | null
}

/**
 * 批量任务配置
 */
export interface BatchConfig {
  globalVoice: GlobalVoiceConfig
  audioFormat: string
  fileVoiceOverrides: FileVoiceConfig[]
  // V1.0移除splitOptions，不再支持文件拆分
}

/**
 * 已处理的文件（UI层）
 */
export interface ProcessedFile {
  id: string
  file: File
  name: string
  size: number
  type: string

  // 文件内容（按需加载）
  rawContent?: string           // 原始内容
  cleanedText?: string          // 清洗后的文本
  contentLoaded?: boolean       // 内容是否已加载

  needParse: boolean
  estimatedChars: number

  // 上传后的状态
  fileUid?: string              // 上传后的文件UID
  uploadStatus?: 'uploading' | 'uploaded' | 'failed'
  uploadProgress?: number       // 上传进度 0-100
  uploadTime?: Date             // 上传时间

  // 解析状态
  parseStatus?: 'pending' | 'parsing' | 'parsed' | 'failed' | 'empty' | 'overlimit' | 'encoding_error'
  parseProgress?: number        // 解析进度 0-100

  // V1.0解析结果
  totalChars?: number           // 实际字符数
  estimateSeconds?: number      // 预计时长（秒）
  estimateCredits?: number      // 预计消耗额度
  encoding?: string             // 文件编码
  baseCharsPerSec?: number      // 基线字符/秒（4.5）
  warnings?: string[]           // 警告信息
  errorMessage?: string         // 错误信息
}

// ============= UI组件Props类型 =============

export interface FileUploadAreaProps {
  loading?: boolean
}

export interface FileUploadAreaEmits {
  'files-selected': [files: File[]]
}



export interface BatchTaskListProps {
  tasks: BatchSession[]
  loading?: boolean
}

export interface BatchTaskListEmits {
  'refresh': []
  'task-click': [task: BatchSession]
}

export interface BatchTaskDetailProps {
  visible: boolean
  task: BatchSession | null
}

export interface BatchTaskDetailEmits {
  'update:visible': [visible: boolean]
  'refresh': []
}

// ============= 工具类型 =============

export interface FileValidationResult {
  valid: boolean
  error?: string
}

export interface UploadProgress {
  loaded: number
  total: number
  percentage: number
}

// ============= 常量定义 =============

/**
 * BatchSession Phase 状态信息
 */
export interface PhaseStatusInfo {
  phase: BatchSessionPhase
  label: string
  color: string
  icon: string
  description: string
}

export const BATCH_SESSION_PHASE_MAP: Record<BatchSessionPhase, PhaseStatusInfo> = {
  draft: {
    phase: 'draft',
    label: '草稿',
    color: '#909399',
    icon: 'Document',
    description: '批次已创建，等待关联文件'
  },
  ready: {
    phase: 'ready',
    label: '就绪',
    color: '#409EFF',
    icon: 'Check',
    description: '文件已关联，准备提交'
  },
  running: {
    phase: 'running',
    label: '运行中',
    color: '#E6A23C',
    icon: 'Loading',
    description: '正在处理任务'
  },
  completed: {
    phase: 'completed',
    label: '已完成',
    color: '#67C23A',
    icon: 'CircleCheck',
    description: '任务已完成'
  },
  failed: {
    phase: 'failed',
    label: '失败',
    color: '#F56C6C',
    icon: 'CircleClose',
    description: '任务处理失败'
  },
  partial: {
    phase: 'partial',
    label: '部分完成',
    color: '#E6A23C',
    icon: 'Warning',
    description: '任务部分成功，部分失败'
  },
  cancelled: {
    phase: 'cancelled',
    label: '已取消',
    color: '#909399',
    icon: 'Close',
    description: '任务已被取消'
  }
}

/**
 * BatchFile 状态信息
 */
export interface FileStatusInfo {
  status: BatchFileStatus
  label: string
  color: string
  icon: string
  description: string
}

export const BATCH_FILE_STATUS_MAP: Record<BatchFileStatus, FileStatusInfo> = {
  uploaded: {
    status: 'uploaded',
    label: '已上传',
    color: '#909399',
    icon: 'Upload',
    description: '文件已上传'
  },
  parsing: {
    status: 'parsing',
    label: '解析中',
    color: '#409EFF',
    icon: 'Loading',
    description: '正在解析文件'
  },
  ready: {
    status: 'ready',
    label: '就绪',
    color: '#67C23A',
    icon: 'Check',
    description: '解析完成，可以提交转换'
  },
  failed: {
    status: 'failed',
    label: '解析失败',
    color: '#F56C6C',
    icon: 'CircleClose',
    description: '文件解析失败'
  }
}

/**
 * WorkItem 状态信息
 */
export interface WorkItemStatusInfo {
  status: WorkItemStatus
  label: string
  color: string
  icon: string
  description: string
}

export const WORK_ITEM_STATUS_MAP: Record<WorkItemStatus, WorkItemStatusInfo> = {
  pending: {
    status: 'pending',
    label: '等待中',
    color: '#909399',
    icon: 'Clock',
    description: '等待执行'
  },
  running: {
    status: 'running',
    label: '执行中',
    color: '#409EFF',
    icon: 'Loading',
    description: '正在执行'
  },
  succeeded: {
    status: 'succeeded',
    label: '成功',
    color: '#67C23A',
    icon: 'CircleCheck',
    description: '执行成功'
  },
  failed: {
    status: 'failed',
    label: '失败',
    color: '#F56C6C',
    icon: 'CircleClose',
    description: '执行失败'
  },
  skipped: {
    status: 'skipped',
    label: '跳过',
    color: '#909399',
    icon: 'Remove',
    description: '已跳过'
  },
  cancelled: {
    status: 'cancelled',
    label: '已取消',
    color: '#909399',
    icon: 'Close',
    description: '已取消'
  }
}

// ============= 状态转移表 =============

/**
 * BatchSession Phase 允许的状态转移
 */
export const BATCH_SESSION_PHASE_TRANSITIONS: Record<BatchSessionPhase, BatchSessionPhase[]> = {
  draft: ['ready', 'cancelled'],
  ready: ['running', 'cancelled'],
  running: ['completed', 'failed', 'partial', 'cancelled'],
  completed: [],
  failed: [],
  partial: [],
  cancelled: []
}

/**
 * BatchFile 允许的状态转移（仅解析相关）
 */
export const BATCH_FILE_STATUS_TRANSITIONS: Record<BatchFileStatus, BatchFileStatus[]> = {
  uploaded: ['parsing', 'ready', 'failed'],
  parsing: ['ready', 'failed'],
  ready: [],  // 解析完成后不再变化
  failed: []  // 失败后不再变化
}

/**
 * WorkItem 允许的状态转移
 */
export const WORK_ITEM_STATUS_TRANSITIONS: Record<WorkItemStatus, WorkItemStatus[]> = {
  pending: ['running', 'skipped', 'cancelled'],
  running: ['succeeded', 'failed', 'cancelled'],
  succeeded: [],
  failed: ['pending'], // 允许重试
  skipped: [],
  cancelled: []
}

// ============= 辅助函数 =============

/**
 * 检查状态转移是否合法
 */
export function isValidPhaseTransition(from: BatchSessionPhase, to: BatchSessionPhase): boolean {
  return BATCH_SESSION_PHASE_TRANSITIONS[from]?.includes(to) ?? false
}

export function isValidFileStatusTransition(from: BatchFileStatus, to: BatchFileStatus): boolean {
  return BATCH_FILE_STATUS_TRANSITIONS[from]?.includes(to) ?? false
}

export function isValidWorkItemStatusTransition(from: WorkItemStatus, to: WorkItemStatus): boolean {
  return WORK_ITEM_STATUS_TRANSITIONS[from]?.includes(to) ?? false
}

/**
 * 获取状态信息
 */
export function getPhaseInfo(phase: BatchSessionPhase): PhaseStatusInfo {
  return BATCH_SESSION_PHASE_MAP[phase]
}

export function getFileStatusInfo(status: BatchFileStatus): FileStatusInfo {
  return BATCH_FILE_STATUS_MAP[status]
}

export function getWorkItemStatusInfo(status: WorkItemStatus): WorkItemStatusInfo {
  return WORK_ITEM_STATUS_MAP[status]
}

/**
 * 判断是否为活跃状态（需要轮询更新）
 */
export function isActivePhase(phase: BatchSessionPhase): boolean {
  return ['ready', 'running'].includes(phase)
}

export function isActiveFileStatus(status: BatchFileStatus): boolean {
  return ['parsing', 'processing'].includes(status)
}

export function isActiveWorkItemStatus(status: WorkItemStatus): boolean {
  return ['pending', 'running'].includes(status)
}

/**
 * 判断是否为终态
 */
export function isFinalPhase(phase: BatchSessionPhase): boolean {
  return ['completed', 'failed', 'partial', 'cancelled'].includes(phase)
}

export function isFinalFileStatus(status: BatchFileStatus): boolean {
  return ['completed', 'failed'].includes(status)
}

export function isFinalWorkItemStatus(status: WorkItemStatus): boolean {
  return ['succeeded', 'failed', 'skipped', 'cancelled'].includes(status)
}

// ============= 文件上传常量 =============

export const SUPPORTED_FILE_TYPES = ['.txt', '.text', '.md', '.srt', '.ass', '.ssa']
export const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
export const MAX_FILE_COUNT = 20
export const MAX_SEGMENT_LENGTH = 2000
export const MIN_SEGMENT_LENGTH = 100
